import { Injectable } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import { FormService } from './form.service';

import {
  IElseStatement,
  IIfStatement,
} from '../../../app/services/api5-service/api.interface';
import { IFormModel } from '../dynamic-form.interface';
import { ISPFieldConfig, ISPFieldTypeWithControl } from '../model';
import { getTypedControls } from '../utils/form-preparing';
import { filterConfigs } from '../utils/formly-configs';
import { isConfigWithControl } from '../utils/is-config-with-control';

export interface Conditional {
  $name: string;
  if?: IIfStatement | IIfStatement[];
  else?: IElseStatement | IElseStatement[];
}

/**
 * Service provides if/else conditions handling for form, control pages/fields/controls disabling and hiding by condition
 */
@UntilDestroy()
@Injectable()
export class ConditionService {
  /** Map of the controls with if/else conditions and their values */
  private controlsAndValues: Record<
    string,
    { value: IFormModel[string]; conditions: Conditional }
  > = {};

  /** Set of names of pages/fields/controls, that should be hidden by condition */
  private readonly hiddenByConditionNamesSubject: BehaviorSubject<Set<string>> =
    new BehaviorSubject(new Set());

  /** Set of disabled by '$shadow' condition rule pages/fields/control names. Can be changed after any form value changing */
  private readonly shadowNamesSubject: BehaviorSubject<Set<string>> =
    new BehaviorSubject(new Set());

  /** Stream of all page/field/control names that hidden by condition */
  readonly hiddenNames$ = this.hiddenByConditionNamesSubject.pipe(
    map(set => Array.from(set)),
  );

  /** Stream of page/field/control names that shadow by condition */
  readonly shadowNames$ = this.shadowNamesSubject.pipe(
    map(set => Array.from(set)),
  );

  /** Get all page/field/control names that hidden by condition */
  get hiddenNames(): string[] {
    return Array.from(this.hiddenByConditionNamesSubject.value);
  }

  /** Get all page/field/control names that shadow by condition */
  get shadowNames(): string[] {
    return Array.from(this.shadowNamesSubject.value);
  }

  constructor(private readonly formService: FormService) {
    this.subscribeToDocChange();
    this.subscribeToValueChanges();
  }

  /**
   * Clear service state
   */
  private clear(): void {
    this.controlsAndValues = {};
    this.hiddenByConditionNamesSubject.next(new Set());
    this.shadowNamesSubject.next(new Set());
  }

  private subscribeToDocChange(): void {
    this.formService.typedForm$
      .pipe(untilDestroyed(this))
      .subscribe(typedForm => {
        this.clear();

        if (!typedForm) {
          return;
        }

        const controls = getTypedControls(typedForm);

        controls.forEach(control => {
          if ('value' in control) {
            const modelValue = this.formService.model[control.control.$name];
            const value = control.value;
            this.handleConditions(
              control.control as Conditional,
              modelValue ?? value,
            );
          }
        });
      });
  }

  private subscribeToValueChanges(): void {
    this.formService.configs$
      .pipe(
        map(configs => filterConfigs(configs, isConfigWithControl)),
        switchMap(configs => {
          return combineLatest(
            configs.map((config: ISPFieldConfig<ISPFieldTypeWithControl>) => {
              const control = config.props.originalControl;
              return this.formService
                .formControlValueChange$(control.$name, true)
                .pipe(
                  distinctUntilChanged(
                    (prev, next) => String(prev) === String(next),
                  ),
                  tap(value => this.handleConditions(control, value)),
                );
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Update(make new) hidden and disabled set
   *
   * @param controlAndValues - some controls metadata and their current value
   */
  private recalculateConditions(
    controlAndValues: Record<
      string,
      { value: IFormModel[string]; conditions: Conditional }
    >,
  ): void {
    const hiddenSet = new Set<string>();
    const shadowSet = new Set<string>();

    for (const [name, { conditions, value }] of Object.entries(
      controlAndValues,
    )) {
      if (hiddenSet.has(name)) {
        // in order to prevent mutual fields hiding
        // when field A hide field B by condition and field B hide field A by condition
        // @WARN potential error place, depending on control names order there can be race error
        continue;
      }

      let isAllConditionsAreFalse = true;

      for (const statement of DocHelper.getArrayWrapperValue(conditions.if)) {
        if (this.isActiveConditionIf(statement, value)) {
          isAllConditionsAreFalse = false;

          if (statement.$hide) {
            if (statement.$shadow) {
              shadowSet.add(statement.$hide);
            } else {
              hiddenSet.add(statement.$hide);
            }
          }
        }
      }

      for (const elseStatement of DocHelper.getArrayWrapperValue(
        conditions.else,
      )) {
        if (isAllConditionsAreFalse) {
          if (elseStatement.$shadow) {
            shadowSet.add(elseStatement.$hide);
          } else {
            hiddenSet.add(elseStatement.$hide);
          }
        }
      }
    }

    this.hiddenByConditionNamesSubject.next(hiddenSet);
    this.shadowNamesSubject.next(shadowSet);
  }

  /**
   * Check that condition is applied
   *
   * @param statement - control if condition
   * @param value - control current value
   */
  private isActiveConditionIf(statement: IIfStatement, value: any): boolean {
    if (statement.$empty === 'no' && value !== '') {
      return true;
    } else if (statement.$empty === 'yes' && value === '') {
      return true;
    } else if (statement.$value === value) {
      return true;
    }
    return false;
  }

  /**
   * Get merged conditions for controls with the same names. Such a union is
   * necessary for the case when BE sends controls in different fields with
   * the same names and different conditions for hiding
   *
   * @param oldConditions
   * @param newConditions
   */
  private getMergedConditions(
    oldConditions: Conditional,
    newConditions: Conditional,
  ): Conditional {
    const oldConditionIf = DocHelper.getArrayWrapperValue(oldConditions.if);
    const oldConditionElse = DocHelper.getArrayWrapperValue(oldConditions.else);

    const newConditionIf = DocHelper.getArrayWrapperValue(newConditions.if);
    const newConditionElse = DocHelper.getArrayWrapperValue(newConditions.else);

    const mergedIf = [...oldConditionIf, ...newConditionIf];
    const mergedElse = [...oldConditionElse, ...newConditionElse];

    return {
      ...newConditions,
      ...(mergedElse.length > 0 ? { else: mergedElse } : {}),
      ...(mergedIf.length > 0 ? { if: mergedIf } : {}),
    };
  }

  /**
   * Add control hide conditions and disabled state to internal state
   *
   * @param conditions - control metadata
   * @param value - control current value
   */
  handleConditions(conditions: Conditional, value: any): void {
    if (conditions.if) {
      const oldCondition = this.controlsAndValues[conditions.$name];
      if (!oldCondition) {
        this.controlsAndValues[conditions.$name] = { conditions, value };
      } else {
        const mergedConditions = this.getMergedConditions(
          oldCondition.conditions,
          conditions,
        );
        this.controlsAndValues[conditions.$name] = {
          conditions: mergedConditions,
          value,
        };
      }

      this.recalculateConditions(this.controlsAndValues);
    }
  }

  /**
   * Check if page/field/control is disabled because of "shadow" state
   *
   * @param name - page/field/control name
   */
  isShadow(name: string[] | string): boolean {
    const names = Array.isArray(name) ? name : [name];
    return names.some(n => this.shadowNamesSubject.value.has(n));
  }

  /**
   * Stream of page/field/control shadow state by name
   *
   * @param name - page/field/control name
   */
  isShadow$(name: string[] | string): Observable<boolean> {
    const names = Array.isArray(name) ? name : [name];
    return this.shadowNamesSubject.pipe(
      map(set => names.some(n => set.has(n))),
      distinctUntilChanged(),
    );
  }

  /**
   * Check if page/field/control is hidden because of some condition
   *
   * @param name - page/field/control name
   */
  isHidden(name: string[] | string): boolean {
    const names = Array.isArray(name) ? name : [name];
    return names.some(n => this.hiddenByConditionNamesSubject.value.has(n));
  }

  /**
   * Stream of page/field/control hiding state by name
   *
   * @param name - page/field/control name
   */
  isHidden$(name: string[] | string): Observable<boolean> {
    const names = Array.isArray(name) ? name : [name];
    return this.hiddenByConditionNamesSubject.pipe(
      map(set => names.some(n => set.has(n))),
      distinctUntilChanged(),
    );
  }
}
